import { MessageSquareText, MessagesSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { ActivityRows } from "@/features/conversations/activity-rows";
import { smsActivity, smsCounts, smsInOrder, smsNumbers } from "@/lib/contact-activity";
import { textsMissing, useContactSms } from "@/lib/queries";
import type { LeadCard } from "@/lib/types";
import { contactName } from "@/lib/contacts";

/**
 * The contact's texts, both ways, from every conversation with them the reader may open
 * (`GET /leads/{id}/sms`): what was said, when, which way, on which of our numbers, who on the team
 * sent it, and whether it arrived. A supervisor or an admin sees every conversation; a rep only the
 * ones their inboxes hold, not a colleague's under Only that rep, nor Unassigned. So an empty tab says
 * there is nothing to show, not that there are no texts. Read-only; replying belongs to
 * Conversations, which the header links to.
 */
export function ContactSmsTab({ contact }: { contact: LeadCard }) {
  const sms = useContactSms(contact.id);
  const name = contactName(contact);
  const reply = (
    <Button asChild variant="outline" size="sm">
      <Link to={`/conversations/team?lead=${contact.id}`}>
        <MessagesSquare className="size-3.5" aria-hidden />
        Open in Conversations
      </Link>
    </Button>
  );

  if (sms.isPending) {
    return (
      <div className="flex justify-center py-8">
        <Spinner className="size-4" />
      </div>
    );
  }
  if (sms.error && textsMissing(sms.error)) {
    return (
      <EmptyState
        icon={MessageSquareText}
        title="Texts aren't on this server yet"
        description="This server does not keep texts, so there are none to show."
      />
    );
  }
  if (sms.error) return <ErrorState error={sms.error} title="Could not load the texts" />;

  const list = sms.data;
  if (list.length === 0) {
    return (
      <EmptyState
        icon={MessageSquareText}
        title="No texts to show"
        description={`Texts to and from ${name ?? "this contact"} will show up here, each with when it was sent and whether it was delivered.`}
        action={reply}
      />
    );
  }

  const counts = smsCounts(list);
  const numbers = smsNumbers(list);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-text-muted">
          {list.length} text{list.length === 1 ? "" : "s"} · {counts.inbound} received · {counts.outbound} sent
          {counts.scheduled > 0 && <> · {counts.scheduled} scheduled</>}
          {counts.failed > 0 && <span className="text-danger-11"> · {counts.failed} not delivered</span>}
          {numbers.length > 1 && <> · on {numbers.length} of our numbers</>}
        </p>
        {reply}
      </div>
      {/* Drawn as Conversations draws them (ACL-591): day by day, the time under each, the rest
          under the time's pointer. */}
      <ActivityRows
        activities={smsInOrder(list).map(smsActivity)}
        contactName={name ?? "Contact"}
        label={`Texts with ${name ?? "this contact"}`}
      />
    </div>
  );
}
